import "../App.scss";

import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateUserData,
  updateMainUserData,
  updateDisplayingHealthForm,
  updateDisplayingTermOfUsePage,
  updateUserToken,
  selectClientAddress,
} from "../store/userSlice";
import { commitGpsFix } from "../v2/utils/address";
import { readGeolocationPermission, requestLocation } from "../v2/utils/resolveLocation";
import { isLoadChanger, holdLoader, releaseLoader, selectAnyLoaderHold } from "../store/mainSlice";
import swalDialog from "../helpers/swalErrorPopup";
import Loader from "./Loader";
import { useNavigate, useLocation } from "react-router-dom";
import userAPI from "../api/userAPI";
import { PARTNER_KEYS, parsePartnerParams } from "../utils/partner";
import { setPartner, setIsOrthodox, setIsMinor, setShowOldClalit } from "../store/partnerStore";

// TEMP-TEST (remove after testing) — all test flags live in src/testSimulation.js.
import { SIMULATE_NEW_USER, SIMULATE_OTP_REQUIRED } from "../testSimulation";

// The handshake's loader-hold key is unique per silentLogin run, not a static
// string: every route wraps its own <LayoutV2>, so "/" → "/home" unmounts one
// instance mid-await and mounts another that runs silentLogin again. With a
// shared key, the dead instance's continuation would release the live one's hold.
let handshakeSeq = 0;

export default function LayoutV2({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoad = useSelector((state) => state.mainSlice.isLoad);
  const anyLoaderHold = useSelector(selectAnyLoaderHold);
  // The hold keys this instance has raised and not yet released, so unmounting
  // mid-handshake can't strand the overlay for whoever renders next.
  const activeHoldsRef = useRef(new Set());
  // Set by a screen that renders fully without the handshake, so the overlay
  // shouldn't cover its background work (see mainSlice.setLoaderSuppressed).
  const suppressLoader = useSelector((state) => state.mainSlice.suppressLoader);
  // Hydrated from localStorage at store construction, so this render's value is
  // already the final one by the time the handshake reads it.
  const clientAddress = useSelector(selectClientAddress);
  const partner = useSelector((state) => state.partnerStore?.partner || PARTNER_KEYS.NONE);

  const getUserDataFromUrl = () => {
    let searchParams = document.location.search;
    // Handle pages without query params such as orderHistory
    if (!searchParams) {
      const queryParams = localStorage.getItem("queryParams");
      if (!queryParams) {
        return null;
      }
      searchParams = queryParams;
    }
    localStorage.setItem("queryParams", searchParams); // Cache the query param in local storage

    // Parsing lives in utils/partner so the "/" dispatcher can read the partner off
    // the URL during render, before this dispatch has happened.
    const { partner: detectedPartner, userData } = parsePartnerParams(searchParams);
    dispatch(setPartner(detectedPartner));
    return userData;
  };

  const handleUserLogin = async (userParams) => {
    const allUserParams = await userAPI.login({
      Token: userParams?.data.Token,
      ID: userParams?.data.ID,
      Phone: userParams?.data.Phone,
    });

    // TEMP-TEST (remove after testing) — see SIMULATE_NEW_USER at the top of the file.
    if (import.meta.env.DEV && SIMULATE_NEW_USER) {
      allUserParams.Street = null;
      allUserParams.HouseNumber = null;
      allUserParams.City = null;
      allUserParams.Latitude = null;
      allUserParams.Longitude = null;
      console.warn("TEST MODE: profile address stripped from the Login response");
    }

    dispatch(updateUserData(allUserParams));

    // A deep link skips home, which is where location is refreshed on a normal
    // entry (useEntryGpsRefresh), and no screen prompts on its own. Without this
    // the venue distance line and the nearest-first sort in the search screen
    // would go blank for the most common way in from the partner app.
    //
    // Only ever when the permission is *already* granted: a dialog must never
    // appear without the user having asked for it by tapping something. So a
    // deep-linked user who has never been asked gets no fix here and falls back to
    // their saved address, then their profile address. permissions.query is
    // prompt-free; where it doesn't exist (Safari < 16) the safe answer is not to
    // resolve.
    const resolveLocationInBackground = async () => {
      try {
        if ((await readGeolocationPermission()) !== "granted") return;

        const result = await requestLocation();
        if (!result.coords) return;

        // Coordinates land in the store synchronously; the address words follow in
        // the background, so the home pill has something to show if the user
        // navigates there from the deep-linked screen.
        commitGpsFix({
          coords: result.coords,
          clientAddress,
          mainUserData: { ...userParams.data, Token: "12" + userParams.data.Token },
          dispatch,
        });
      } catch {
        // Unsupported or blocked — leave the location to home's entry refresh.
      }
    };

    const clubId = userParams.data.ClubId;
    if (clubId > 0) {
      resolveLocationInBackground();
      navigate(`/venue/${clubId}`, {
        state: {
          price: "",
          clubId: clubId,
          clubName: "",
          ignoreApiCall: partner === PARTNER_KEYS.MEHUDET || partner === PARTNER_KEYS.LEUMIT,
        },
      });
    } else if (userParams.data.OpenMyOrders) {
      resolveLocationInBackground();
      navigate("/orderHistory");
    }
    // No deep-link branch for the default destination: "/" already renders home,
    // so there is nothing to redirect to from here.

    // Deliberately not an `else` — the health form outranks every branch above.
    // No fromOnboarding state: this path serves existing users (renewal), whose
    // questionnaire must exit to home, not to the one-time welcome intro.
    if (userParams?.data.ShowHealthConditionStatementForm) {
      navigate("/registrationQuestionnaire");
    }
  };

  const handlePhoneValidation = (userParams) => {
    navigate("/phone-validation", {
      state: {
        IsFreefitWeb: true,
        ClaimNumber: userParams.data.ClaimNumber,
        FirstName: userParams.data.FirstName,
        LastName: userParams.data.LastName,
        Email: userParams.data.Email,
        IsOrthoDox: userParams.data.IsOrthoDox,
        Gender: userParams.data.Gender,
        IsMinor: userParams.data?.IsMinor,
      },
    });
    dispatch(isLoadChanger(false));
  };

  const silentLogin = async () => {
    // The overlay hold for this handshake run. A keyed hold rather than
    // isLoadChanger: the V2 home gate holds the same overlay concurrently, and
    // with the single boolean whichever owner finished first hid it for both
    // (Login could resolve while the GetCarousels call was still in flight).
    // Releasing an un-raised key is a no-op, so the catch can release blindly.
    const holdKey = `handshake:${++handshakeSeq}`;
    const release = () => {
      activeHoldsRef.current.delete(holdKey);
      dispatch(releaseLoader(holdKey));
    };

    try {
      // Check if we're already on the partner error page to avoid infinite redirects
      if (location.pathname === "/partner-error") {
        dispatch(isLoadChanger(false));
        return;
      }

      const UserData = getUserDataFromUrl();

      if (!UserData) {
        dispatch(isLoadChanger(false));
        navigate("/partner-error");
        return;
      }

      // Check if no partner data is available and redirect to error page
      if (!localStorage.getItem("userToken")) {
        // The live URL only — a first visit must carry its own partner param, not
        // inherit one from a cached query string.
        const hasValidPartnerParam = parsePartnerParams(document.location.search).partner !== PARTNER_KEYS.NONE;

        if (!hasValidPartnerParam) {
          dispatch(isLoadChanger(false));
          navigate("/partner-error");
          return;
        }
      }

      if (UserData) {
        localStorage.setItem("userToken", UserData);
      }

      // TEMP-TEST (remove after testing) — see SIMULATE_OTP_REQUIRED in testSimulation.js.
      if (import.meta.env.DEV && SIMULATE_OTP_REQUIRED) {
        console.warn("TEST MODE: forcing the OTP flow with a mock payload");
        dispatch(isLoadChanger(false));
        // Not when already inside the flow — the real IsOtpRequired flag flips
        // after the code is sent, so re-navigating from the OTP screen would
        // make it untestable.
        if (location.pathname !== "/phone-validation" && location.pathname !== "/otp-validation") {
          handlePhoneValidation({
            data: {
              ClaimNumber: 123456789,
              FirstName: "ישראל",
              LastName: "",
              Email: "",
              IsOrthoDox: false,
              Gender: 1,
              IsMinor: false,
            },
          });
        }
        return;
      }

      activeHoldsRef.current.add(holdKey);
      dispatch(holdLoader(holdKey));

      const userParams = await userAPI.getUserData(UserData || localStorage.getItem("userToken"));

      if (userParams.data?.IsOrthoDox != null) {
        dispatch(setIsOrthodox(userParams.data.IsOrthoDox));
      }

      if (userParams.data?.IsMinor != null) {
        dispatch(setIsMinor(userParams.data.IsMinor));
      }

      // The V2 kill switch (GlobalSettings.ShowOldClalit on the service). Dispatched before the
      // IsOtpRequired early return below, so a user who lands on OTP still has the flag stored for
      // the screens that follow. The reducer persists it; isV2Enabled reads it synchronously.
      if (userParams.data?.ShowOldClalit != null) {
        dispatch(setShowOldClalit(userParams.data.ShowOldClalit));
      }

      if (userParams.data.IsOtpRequired) {
        release();
        handlePhoneValidation(userParams);
        return;
      }

      if (userParams.data?.CorrelationId) {
        localStorage.setItem("CorrelationId", userParams.data?.CorrelationId);
      }

      dispatch(updateMainUserData({ ...userParams.data, Token: "12" + userParams.data.Token }));
      dispatch(updateDisplayingHealthForm(userParams.data.ShowHealthConditionStatementForm));
      dispatch(updateDisplayingTermOfUsePage(userParams.data.ShowTermOfUseFormConditionStatementForm));
      dispatch(updateUserToken(userParams.data.Token));

      if (userParams.data.Email === "") {
        navigate("/registration");
      } else {
        await handleUserLogin(userParams);
      }

      release();
    } catch (err) {
      release();

      // If we're already on the partner error page, don't show additional errors
      if (location.pathname === "/partner-error") {
        return;
      }

      // Check if the error is related to partner authentication
      // If no valid partner is detected and API fails, redirect to error page
      if (partner === PARTNER_KEYS.NONE) {
        navigate("/partner-error");
        return;
      }

      // For other errors, show the standard error dialog
      swalDialog(err);
    }
  };

  useEffect(() => {
    silentLogin();
  }, []);

  // Unmount mid-handshake (route change swaps in a fresh LayoutV2 instance):
  // release whatever this instance still holds. The late async continuation's own
  // release then no-ops, and the next instance raises its own key.
  useEffect(
    () => () => {
      activeHoldsRef.current.forEach((key) => dispatch(releaseLoader(key)));
      activeHoldsRef.current.clear();
    },
    [dispatch],
  );

  useEffect(() => {
    if (partner === PARTNER_KEYS.MAOFLAAMIT) {
      document.body.style.backgroundColor = "#ffffff";
      return () => {
        document.body.style.backgroundColor = "";
      };
    }
  }, [partner]);

  return (
    <>
      {(isLoad || anyLoaderHold) && !suppressLoader ? <Loader /> : ""}
      <>{children}</>
    </>
  );
}
